import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Modal, ConfirmationModal } from '../../components/Modal';
import { apiService } from '../../services/api';
import { cancelLocalReminders } from '../../services/calendar-local-reminders.service';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { colors, spacing, textStyles } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface EventQuickViewModalProps {
  visible: boolean;
  event: any | null;
  businessId?: string;
  onClose: () => void;
  onEdit: (eventId: string) => void;
  onDeleted: (eventId: string) => void;
  onNotify: (type: 'success' | 'error', title: string, message: string) => void;
}

/**
 * A Google-Calendar-style quick view — tapping an event in the month grid
 * opens this instead of navigating straight to the full editor. Mirrors
 * CalendarBookingScreen.tsx's own inline Event Edit Modal pattern (title/
 * time/location/description in a Modal) and EventDetailScreen.tsx's
 * delete flow (ConfirmationModal + apiService.deleteEvent +
 * cancelLocalReminders), so both existing UX patterns are reused rather
 * than reinvented here.
 */
export const EventQuickViewModal: React.FC<EventQuickViewModalProps> = ({
  visible,
  event,
  businessId,
  onClose,
  onEdit,
  onDeleted,
  onNotify,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!event) return null;

  const startDate = typeof event.startDate === 'string' ? new Date(event.startDate) : event.startDate;
  const endDate = typeof event.endDate === 'string' ? new Date(event.endDate) : event.endDate;
  const isSameDay = startDate && endDate && startDate.toDateString() === endDate.toDateString();

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const formatTime = (date: Date) =>
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  const handleDelete = async () => {
    if (!event._id) return;
    try {
      setDeleting(true);
      const response = await apiService.deleteEvent(event._id, businessId);
      if (response.statusCode === 1000) {
        cancelLocalReminders(event._id).catch((err) =>
          console.error('[EventQuickViewModal] Failed to cancel local reminders:', err)
        );
        onNotify('success', t('calendar.eventDetail.deleteSuccess'), '');
        setShowDeleteModal(false);
        onDeleted(event._id);
      } else {
        onNotify('error', t('calendar.eventDetail.deleteFailed'), response.errors?.[0]?.message || '');
      }
    } catch (error: any) {
      onNotify('error', t('calendar.eventDetail.deleteFailed'), error?.message || '');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* RN's Modal renders as a separate native/portal layer — having two mounted
          simultaneously (this one + ConfirmationModal below) is unreliable across
          platforms, so hide this one while the delete confirmation is up rather
          than stacking two RNModal instances at once. */}
      <Modal visible={visible && !showDeleteModal} onClose={onClose} title={event.title || t('calendar.eventDetail.title')} size="medium">
        <View style={styles.section}>
          <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="time-outline" size={18} color={colors.primary as string} />
            <View style={rtl.marginStart(spacing.sm)}>
              <Text style={styles.detailText}>{formatDate(startDate)}</Text>
              {event.isAllDay ? (
                <Text style={styles.detailSubtext}>{t('calendar.eventDetail.allDay')}</Text>
              ) : (
                <Text style={styles.detailSubtext}>
                  {formatTime(startDate)} - {formatTime(endDate)}
                  {!isSameDay ? ` (${formatDate(endDate)})` : ''}
                </Text>
              )}
            </View>
          </View>

          {event.meetingType === 'offline' && event.location?.name && (
            <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="location-outline" size={18} color={colors.primary as string} />
              <Text style={[styles.detailText, rtl.marginStart(spacing.sm)]}>{event.location.name}</Text>
            </View>
          )}

          {event.description ? (
            <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="document-text-outline" size={18} color={colors.primary as string} />
              <Text style={[styles.detailText, rtl.marginStart(spacing.sm)]}>{event.description}</Text>
            </View>
          ) : null}
        </View>

        <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity
            style={[styles.actionButton, { flexDirection: rtl.rowDirection }]}
            onPress={() => onEdit(event._id)}
          >
            <Ionicons name="create-outline" size={18} color={colors.primary as string} />
            <Text style={[styles.actionButtonText, rtl.marginStart(spacing.xs)]}>{t('calendar.eventDetail.editButton')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionButton, { flexDirection: rtl.rowDirection }]}
            onPress={() => setShowDeleteModal(true)}
          >
            <Ionicons name="trash-outline" size={18} color={colors.error as string} />
            <Text style={[styles.actionButtonText, rtl.marginStart(spacing.xs), { color: colors.error as string }]}>
              {t('calendar.eventDetail.deleteButton')}
            </Text>
          </TouchableOpacity>
        </View>
      </Modal>

      <ConfirmationModal
        visible={showDeleteModal}
        title={t('calendar.eventDetail.deleteTitle')}
        message={t('calendar.eventDetail.deleteMessage')}
        confirmText={t('calendar.eventDetail.deleteConfirm')}
        cancelText={t('calendar.eventDetail.deleteCancel')}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
        loading={deleting}
        type="error"
      />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  section: {
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  row: {
    alignItems: 'flex-start',
  },
  detailText: {
    ...textStyles.body,
    fontWeight: textStyles.body.fontWeight as any,
    color: colors.textDark,
  },
  detailSubtext: {
    ...textStyles.caption,
    fontWeight: textStyles.caption.fontWeight as any,
    color: colors.textLight,
    marginTop: spacing.xs / 2,
  },
  actions: {
    gap: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  actionButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
  },
  actionButtonText: {
    ...textStyles.body,
    fontWeight: '600',
    color: colors.primary as string,
  },
});
